'use client'

import * as React from 'react';
import { Code as CodeIcon } from 'lucide-react';
import { ToolCall, SQLQueryResponse, SchemaResponse } from '../types';
import { SqlQueryResult } from './sql-query-result';
import { SchemaResult } from './schema-result';
import { useNovaStoreContext } from '../../nova/nova-store-context';
import { ToolLoadingState } from './tool-loading-state';
import { cn } from '../../../../components/ui';

/**
 * Tool Invocation Component
 * 
 * Displays a tool invocation and its result.
 */
export function ToolInvocation({ 
  toolCall, 
  onRunSqlQuery
}: { 
  toolCall: ToolCall, 
  onRunSqlQuery?: (query: string) => void
}) {
  // Extract tool information
  const toolName = toolCall.name;
  const toolParameters = toolCall.parameters || {};
  
  // Get the Nova store to access panel widths
  const store = useNovaStoreContext();
  const chatPanelWidth = store.panelWidths.chat;
  
  // Create a normalized result object
  const [result, setResult] = React.useState<SQLQueryResponse | SchemaResponse | null>(null);
  
  // Update result when toolCall changes
  React.useEffect(() => {
    if (toolCall.result) {
      setResult(toolCall.result);
    }
  }, [toolCall]);
  
  // Render SQL query result
  const renderSqlQueryResult = () => {
    if (!result) return null;
    
    // Add the query to the result if it's not already there
    const sqlResult: SQLQueryResponse = {
      ...result as SQLQueryResponse,
      query: typeof toolParameters.query === 'string' ? toolParameters.query : (result as SQLQueryResponse).query
    };
    
    // Calculate the appropriate width based on the chat panel width
    // Subtract padding to ensure the component fits within the container
    // p-4 padding is 16px on each side (32px total), plus any existing margin (24px)
    const sqlWidth = chatPanelWidth > 0 ? chatPanelWidth - 56 : 400;
    
    
    return (
      <SqlQueryResult 
        result={sqlResult} 
        onRunSqlQuery={onRunSqlQuery}
        width={sqlWidth}
        height={300}
      />
    );
  };
  
  // Render schema result
  const renderSchemaResult = () => {
    if (!result) return null;
    
    return <SchemaResult result={result as SchemaResponse} />;
  };
  
  // Render loading state for in-progress tool calls
  const renderLoadingState = () => {
    return <ToolLoadingState toolName={toolName} />;
  };
  
  // Render error state
  const renderErrorState = () => {
    return (
      <div className="flex items-center justify-center p-4 text-muted-foreground bg-muted/50 rounded-md border border-border">
        <span className="text-xs">Error processing {toolName}</span>
      </div>
    );
  };
  
  const renderToolResult = () => {
    // Check if the tool is still running or waiting
    const inProgressStates = ['running', 'waiting', 'call', 'partial-call'];
    if (!result || (toolCall.state && inProgressStates.includes(toolCall.state as string))) {
      return renderLoadingState();
    }

    // Check if there was an error
    if (!result.success) {
      // Don't show error details to the user, just log them for debugging
      console.warn('Tool execution error:', result.error);
      
      // For SQL query errors, log the query that caused the error
      if (toolName === 'sqlQuery' && (result as SQLQueryResponse).query) {
        console.warn('Query that caused the error:', (result as SQLQueryResponse).query);
      }
      
      return renderErrorState();
    }

    // Handle successful SQL query result
    if (toolName === 'sqlQuery') {
      return renderSqlQueryResult();
    }

    // Handle successful schema result
    if (toolName === 'getSchema') {
      return renderSchemaResult();
    }

    // Fallback for unknown tool types
    return (
      <pre className="bg-muted p-3 rounded-md text-xs overflow-auto text-foreground">
        {JSON.stringify(result, null, 2)}
      </pre>
    );
  };


  return (
    <div className="mt-3 rounded-md overflow-hidden w-full flex flex-col">
      <div className="w-full overflow-hidden flex-1">
        {renderToolResult()}
      </div>
    </div>
  );
} 